import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import {
  Box,
  CircularProgress,
  Typography,
  useTheme,
  useMediaQuery,
  Grid,
  Pagination,
  Stack,
} from '@mui/material';

import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineSharpIcon from '@mui/icons-material/DeleteOutlineSharp';
import IconButton from '@mui/material/IconButton';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import RemoveCircleOutlineSharpIcon from '@mui/icons-material/RemoveCircleOutlineSharp';

import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';

import nodatafound from '../../utilities/dummy_assets/nodatafound.jpg';
import { ImageBaseURL } from '../../utilities/api/axios';

import InventoryListAPIs from '../../utilities/api/inventory/AdminInventoryList';
import InventoryDetailAPIs from '../../utilities/api/inventory/AdminInventoryDetail';

import Delete from './Popups/Delete';
import EditInventoryDialog from './Popups/EditInventory';
import AddStock from './Popups/AddStock';
import RemoveStock from './Popups/RemoveStock';

import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import CustomPagination from '../CustomPagination/CustomPagination';
import TuneIcon from '@mui/icons-material/Tune';
import CommonInventoryCard from './CommonInventoryCard';
import { dataGridSx } from './MachineTable';

/* ------- Card Fields -----*/

const inventoryFields = [
  { label: 'Group', value: 'group' },
  { label: 'Category', value: 'category', type: 'chip' },
  { label: 'Manufacturer', value: 'manufacturer' },
  { label: 'Location', value: 'location' },
  { label: 'Availability', value: 'availability' },
  { label: 'Status', value: 'status' },
  { label: 'Supervised', value: r => (r.supervised ? 'Yes' : 'No') },
  { label: 'Description', value: 'description', type: 'tooltip' },
  {
    label: 'Stock',
    value: r =>
      `${r.stock_available} ${r.stock_unit !== 'number' ? r.stock_unit : ''}`,
  },
  {
    label: 'Total Stock',
    value: r =>
      `${r.stock_total || 0} ${r.stock_unit !== 'number' ? r.stock_unit : ''}`,
  },
  { label: 'Edit Stock', type: 'stockActions' },
];

/* COLUMNS (UNCHANGED) */

const columns = (
  handleEditInventory,
  handleOpenDeletePopup,
  handleOpenAddStockPopup,
  handleOpenRemoveStockPopup
) => [
  {
    field: 'name',
    headerName: 'Inventory Name',
    minWidth: 200,
    flex: 1.5,
    headerAlign: 'left',
    headerClassName: 'header-style',
    renderCell: params => {
      const imageUrl =
        params.row && params.row.image
          ? params.row.image.startsWith('http')
            ? params.row.image
            : `${ImageBaseURL}${params.row.image}`
          : nodatafound;
      const text = params.row && params.row.name ? params.row.name : 'No Text';
      const subtext =
        params.row && params.row.upc ? params.row.upc : 'No Subtext';
      return (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1,
          }}
        >
          <img
            src={imageUrl}
            alt={params.row.name}
            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
          />
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" sx={{ color: '#434343' }}>
              {text}
            </Typography>
            <Typography variant="body2" sx={{ color: '#8C8C8C' }}>
              {subtext}
            </Typography>
          </Box>
        </Box>
      );
    },
  },
  {
    field: 'group',
    headerName: 'Group',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.group || '-'}
      </Typography>
    ),
  },
  {
    field: 'category',
    headerName: 'Category',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box
        sx={{
          border: '1px solid #E6E6E6',
          borderRadius: '50px',
          padding: '4px 8px',
        }}
      >
        <Typography sx={{ color: '#8C8C8C', textAlign: 'left', width: '100%' }}>
          {params.row.category}
        </Typography>
      </Box>
    ),
  },
  {
    field: 'manufacturer',
    headerName: 'Manufacturer',
    minWidth: 120,
    flex: 1,
    headerAlign: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.manufacturer}
      </Typography>
    ),
  },
  {
    field: 'location',
    headerName: 'Location',
    width: 150,
    headerAlign: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography
        sx={{
          color: '#434343',
          textAlign: 'left',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={params.row.location || '-'}
      >
        {params.row.location || '-'}
      </Typography>
    ),
  },
  {
    field: 'availability',
    headerName: 'Availability',
    width: 130,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => {
      const availabilityList = [
        'Moderate',
        'Very Low',
        'Low',
        'High',
        'Always',
      ];
      let availabilityValue = '-';

      // Handle both numeric (0-4) and text values
      if (
        params.row.availability !== undefined &&
        params.row.availability !== null &&
        params.row.availability !== ''
      ) {
        if (typeof params.row.availability === 'number') {
          availabilityValue = availabilityList[params.row.availability] || '-';
        } else {
          availabilityValue = params.row.availability;
        }
      }

      return (
        <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
          {availabilityValue}
        </Typography>
      );
    },
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => {
      const statusList = ['Operational', 'Under Maintenance', 'Out of Service'];
      let statusValue = '-';

      // Handle both numeric (0-2) and text values
      if (
        params.row.status !== undefined &&
        params.row.status !== null &&
        params.row.status !== ''
      ) {
        if (typeof params.row.status === 'number') {
          statusValue = statusList[params.row.status] || '-';
        } else {
          statusValue = params.row.status;
        }
      }

      return (
        <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
          {statusValue}
        </Typography>
      );
    },
  },
  {
    field: 'supervised',
    headerName: 'Supervised',
    width: 110,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.supervised ? 'Yes' : 'No'}
      </Typography>
    ),
  },
  {
    field: 'description',
    headerName: 'Description',
    minWidth: 200,
    flex: 2,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography
        sx={{
          color: '#434343',
          textAlign: 'left',
          width: '100%',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        title={params.row.description || '-'}
      >
        {params.row.description}
      </Typography>
    ),
  },
  {
    field: 'stock_available',
    headerName: 'Stock',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.stock_available}{' '}
        {params.row.stock_unit !== 'number' ? params.row.stock_unit : ''}
      </Typography>
    ),
  },
  {
    field: 'stock_total',
    headerName: 'Total Stock',
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Typography sx={{ color: '#434343', textAlign: 'left', width: '100%' }}>
        {params.row.stock_total || 0}{' '}
        {params.row.stock_total !== 'number' ? params.row.stock_unit : ''}
      </Typography>
    ),
  },
  {
    field: 'editstock',
    headerName: 'Edit stock',
    sortable: false,
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <IconButton
          aria-label="add"
          onClick={() => handleOpenAddStockPopup(params.row)}
        >
          <AddCircleOutlineOutlinedIcon sx={{ color: 'green' }} />
        </IconButton>
        <IconButton
          aria-label="remove"
          onClick={() => handleOpenRemoveStockPopup(params.row)}
        >
          <RemoveCircleOutlineSharpIcon sx={{ color: '#C90505' }} />
        </IconButton>
      </Box>
    ),
  },
  {
    field: 'action',
    headerName: 'Action',
    sortable: false,
    width: 100,
    headerAlign: 'left',
    align: 'left',
    headerClassName: 'header-style',
    renderCell: params => (
      <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <IconButton
          aria-label="edit"
          onClick={() => handleEditInventory(params.row)}
        >
          <EditIcon />
        </IconButton>
        <IconButton
          aria-label="delete"
          onClick={() => handleOpenDeletePopup(params.row)}
        >
          <DeleteOutlineSharpIcon sx={{ color: '#C90505' }} />
        </IconButton>
      </Box>
    ),
  },
];

const CustomNoRowsOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <img
      src={nodatafound}
      alt="No Data Found"
      style={{ width: '250px', marginBottom: '8px' }}
    />
    <Typography variant="body1" color="textSecondary">
      No data available
    </Typography>
  </Box>
);

const CustomLoadingOverlay = () => (
  <Box
    sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100%',
    }}
  >
    <CircularProgress size={40} />
    <Typography sx={{ mt: 2 }} color="textSecondary">
      Updating list...
    </Typography>
  </Box>
);

const InventoryTable = ({
  searchQuery,
  category,
  group,
  tag,
  update,
  setUpdate,
  flag,
  setFlag,
  setTableDataProp,
  status,
  supervised,
}) => {
  const user = useSelector(state => state.user.user);
  const token = React.useMemo(() => user.access, [user]);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));

  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [rowCount, setRowCount] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  const [openDeletePopup, setOpenDeletePopup] = useState(false);
  const [currentRow, setCurrentRow] = useState(null);

  const [isEditInventoryOpen, setEditInventoryOpen] = useState(false);
  const [openAddStockPopup, setopenAddStockPopup] = useState(false);
  const [openRemoveStockPopup, setopenRemoveStockPopup] = useState(false);

  // ✅ 1. Reset page to 0 whenever search query or filters change
  useEffect(() => {
    setPaginationModel(prev => ({ ...prev, page: 0 }));
  }, [searchQuery, category, group, tag, status, supervised]);

  // ✅ 2. Fetch data from server based on pagination and filters
  useEffect(() => {
    setLoading(true);

    InventoryListAPIs.InventoryListGet({
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
      search: searchQuery,
      category: category,
      group: group,
      tag: tag,
      status: status,
      supervised: supervised,
    })
      .then(result => {
        // Backend returns { count, next, previous, results }
        const data = result.results || [];
        setTableData(data);
        setRowCount(result.count || 0);
        if (setTableDataProp) setTableDataProp(data);
      })
      .catch(() => toast.error('Error fetching inventory data'))
      .finally(() => setLoading(false));
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    searchQuery,
    category,
    group,
    tag,
    status,
    supervised,
    update,
    user,
    token,
  ]);

  // Rows for DataGrid (No longer filtering locally)
  const rows = tableData.map((item, index) => ({ ...item, index }));

  const handleEditInventory = row => {
    setCurrentRow(row);
    setEditInventoryOpen(true);
  };

  const handleOpenDeletePopup = row => {
    setCurrentRow(row);
    setOpenDeletePopup(true);
  };

  const handleOpenAddStockPopup = row => {
    setCurrentRow(row);
    setopenAddStockPopup(true);
  };

  const handleOpenRemoveStockPopup = row => {
    setCurrentRow(row);
    setopenRemoveStockPopup(true);
  };

  const handleDelete = () => {
    InventoryDetailAPIs.InventoryDetailDelete(currentRow.id, token)
      .then(() => {
        setUpdate(prev => !prev);
        toast.success('Item successfully deleted');
      })
      .catch(() => toast.error('Error deleting item'));

    setOpenDeletePopup(false);
  };

  /* CARD PAGINATION */
  const cardsPerPage = paginationModel.pageSize;
  const totalPages = Math.ceil(rowCount / cardsPerPage);

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        padding: '16px',
        minHeight: '550px',
      }}
    >
      {isMobile || isTablet ? (
        /* ---------- MOBILE/TABLET CARDS ---------- */
        <Box position="relative" sx={{ minHeight: '400px' }}>
          {loading && (
            <Box
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'rgba(255, 255, 255, 0.7)',
                minHeight: '200px',
              }}
            >
              <CircularProgress size={40} />
              <Typography mt={2}>Updating list...</Typography>
            </Box>
          )}

          {rows.length === 0 && !loading ? (
            <Box
              textAlign="center"
              py={10}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              <img src={nodatafound} alt="No data found" width="250" />
              <Typography color="textSecondary" mt={1}>
                No data available
              </Typography>
            </Box>
          ) : (
            <>
              <Grid container spacing={2}>
                {rows.map(row => (
                  <Grid item xs={12} sm={6} key={row.id}>
                    <CommonInventoryCard
                      row={row}
                      fields={inventoryFields}
                      onEdit={handleEditInventory}
                      onDelete={handleOpenDeletePopup}
                      onAddStock={handleOpenAddStockPopup}
                      onRemoveStock={handleOpenRemoveStockPopup}
                    />
                  </Grid>
                ))}
              </Grid>

              <Stack alignItems="center" mt={3}>
                <Pagination
                  count={totalPages}
                  page={paginationModel.page + 1}
                  onChange={(e, value) =>
                    setPaginationModel(prev => ({ ...prev, page: value - 1 }))
                  }
                  color="primary"
                />
              </Stack>
            </>
          )}
        </Box>
      ) : (
        /* ---------- DESKTOP TABLE ---------- */
        <Box sx={{ height: 600, width: '100%' }}>
          <DataGrid
            loading={loading}
            rows={rows}
            rowHeight={85}
            columns={columns(
              handleEditInventory,
              handleOpenDeletePopup,
              handleOpenAddStockPopup,
              handleOpenRemoveStockPopup
            )}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            pageSizeOptions={[7, 10, 25]}
            slots={{
              pagination: CustomPagination,
              noRowsOverlay: CustomNoRowsOverlay,
              loadingOverlay: CustomLoadingOverlay,
              columnMenuIcon: () => (
                <TuneIcon
                  sx={{
                    transform: 'rotate(90deg)',
                    fill: '#8C8C8C',
                    height: '20px',
                    width: '18px',
                  }}
                />
              ),
            }}
            disableDensitySelector
            sx={dataGridSx}
          />
        </Box>
      )}

      <AddStock
        currentRow={currentRow}
        open={openAddStockPopup}
        onClose={() => setopenAddStockPopup(false)}
        update={update}
        setUpdate={setUpdate}
      />

      <RemoveStock
        currentRow={currentRow}
        open={openRemoveStockPopup}
        onClose={() => setopenRemoveStockPopup(false)}
        update={update}
        setUpdate={setUpdate}
      />

      <Delete
        currentRow={currentRow}
        open={openDeletePopup}
        onClose={() => setOpenDeletePopup(false)}
        onConfirm={handleDelete}
      />

      <EditInventoryDialog
        currentRow={currentRow}
        open={isEditInventoryOpen}
        onClose={() => setEditInventoryOpen(false)}
        update={update}
        setUpdate={setUpdate}
        flag={flag}
        setFlag={setFlag}
      />

      <ToastContainer />
    </Box>
  );
};

export default InventoryTable;
